<template>
  <BuilderWrapper
    title="Numeric Formatting"
    helpful-tip="These settings apply across the entire app."
    :hide-tip="exclude.includes('paragraph')"
    :smaller-title="!exclude.includes('h5')"
  >
    <LabelledInput label="Numeric Formatting">
      <LabelledInput
        v-if="!exclude.includes('locale')"
        label="Locale"
        helpful-tip="Choose the locale to use for number formatting. If the locale you wish to use is not present, please contact OrbViz."
      >
        <b-select
          v-model="numericFormattingLocale"
          :options="localeOptions"
        />
      </LabelledInput>

      <LabelledInput
        label="Decimal places"
        helpful-tip="Choose the number of decimal places to use for numeric formatting"
      >
        <b-input
          v-model="numericFormattingNumDigits"
          type="number"
          min="0"
          max="20"
          data-cy="decimal-input"
        />
      </LabelledInput>

      <LabelledInput>
        Numeric example: {{ numericSample }}
      </LabelledInput>
    </LabelledInput>

    <LabelledInput label="Monetary Formatting">
      <LabelledInput
        v-if="!exclude.includes('currency')"
        label="Currency"
        helpful-tip="Choose the currency to use for monetary formatting"
      >
        <b-select
          v-model="monetaryFormattingCurrency"
          :options="currencyOptions"
          data-cy="currency-input"
        />
      </LabelledInput>

      <LabelledInput
        label="Decimal places"
        helpful-tip="Choose the number of decimal places to use for monetary formatting"
      >
        <b-input
          v-model="monetaryFormattingNumDigits"
          type="number"
          min="0"
          max="20"
          data-cy="decimal-input"
        />
      </LabelledInput>

      <LabelledInput>
        Monetary example: {{ monetarySample }}
      </LabelledInput>
    </LabelledInput>
  </BuilderWrapper>
</template>

<script lang="ts">

import Vue, { PropType } from 'vue'
import Component from 'vue-class-component'

import BuilderWrapper from '@/components/builder/UI/BuilderWrapper.vue'
import LabelledInput from '@/components/builder/UI/LabelledInput.vue'

import { MonetaryFormattingConfig, NumericFormattingConfig } from '@/types'
import {
  NUMERIC_FORMATTING_CONFIG_KEY,
  MONETARY_FORMATTING_CONFIG_KEY,
} from '@/constants'

const components = {
  BuilderWrapper,
  LabelledInput,
}

const props = {
  exclude: {
    type: Array as PropType<string[]>,
    default: () => ['h5'],
  },
}

@Component({ components })
export default class NumericFormatting extends Vue.extend({ props }) {
  localeOptions: { text: string, value: string }[] = [
    { text: 'English - New Zealand', value: 'en-nz' },
    { text: 'English - Australia', value: 'en-AU' },
    { text: 'English - US', value: 'en-US' },
    { text: 'English - GB', value: 'en-GB' },
    { text: 'German - Germany', value: 'de-DE' },
  ]

  currencyOptions: { text: string, value: string }[] = [
    { text: 'NZD', value: 'NZD' },
    { text: 'AUD', value: 'AUD' },
    { text: 'USD', value: 'USD' },
    { text: 'GBP', value: 'GBP' },
    { text: 'EUR', value: 'EUR' },
  ]

  get numericFormattingLocale () {
    return this.$typedStore.primary.app.config.numericFormatting.locale
  }

  set numericFormattingLocale (value) {
    this.updateConfig(value, NUMERIC_FORMATTING_CONFIG_KEY, 'locale')
  }

  get numericFormattingNumDigits () {
    return this.$typedStore.primary.app.config.numericFormatting.numDigits
  }

  set numericFormattingNumDigits (value) {
    this.updateConfig(value, NUMERIC_FORMATTING_CONFIG_KEY, 'numDigits')
  }

  get monetaryFormattingCurrency () {
    return this.$typedStore.primary.app.config.monetaryFormatting.currency
  }

  set monetaryFormattingCurrency (value) {
    this.updateConfig(value, MONETARY_FORMATTING_CONFIG_KEY, 'currency')
  }

  get monetaryFormattingNumDigits () {
    return this.$typedStore.primary.app.config.monetaryFormatting.numDigits
  }

  set monetaryFormattingNumDigits (value) {
    this.updateConfig(value, MONETARY_FORMATTING_CONFIG_KEY, 'numDigits')
  }

  get monetarySample () {
    return this.$typedStore.visualisationApp.monetaryFormatting(12345.4321)
  }

  get numericSample () {
    return this.$typedStore.visualisationApp.numericFormatting(12345.4321)
  }

  updateConfig (value: Intl.NumberFormatOptions['currency'] | number | string, ...args: [typeof NUMERIC_FORMATTING_CONFIG_KEY, keyof NumericFormattingConfig] | [typeof MONETARY_FORMATTING_CONFIG_KEY, keyof MonetaryFormattingConfig]) {
    this.$typedStore.primary.app.updateConfig({
      configPath: args,
      value,
    })
  }
}

</script>
